import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog, DialogContentHighPriority, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Wifi, WifiOff, AlertTriangle, Loader2, MapPin, Upload, ImageIcon, X, Building2 } from 'lucide-react';
import {
  resolverMovelSemFibra, resolverSoFibra, type ResolucaoModal,
} from '@/lib/convergencia';
import { type AvaliacaoPedido, coberturaPorCep } from '@/lib/convergenciaPedido';
import { supabase } from '@/integrations/supabase/client';
import { parseCnpjPayload } from '@/utils/parseCnpjPayload';
import { ImageLightbox } from '@/components/common/ImageLightbox';
import { AudioPlayer } from '@/components/common/AudioPlayer';
import { toast } from 'sonner';

export type ResolverConvergencia = ResolucaoModal & {
  cepInformado?: string | null;
  coberturaInformada?: boolean | null;
};

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  aval: AvaliacaoPedido | null;
  processing?: boolean;
  /** Decisão final: parent grava e (passa→fecha) ou (gerente→não fecha). */
  onResolver: (r: ResolverConvergencia) => void;
}

type Modo = 'cep' | 'movel_sem_fibra' | 'so_fibra';

export function ModalConvergencia({ open, onOpenChange, aval, processing, onResolver }: Props) {
  const modoInicial: Modo = useMemo(() => {
    switch (aval?.gate.acao) {
      case 'precisa_cep': return 'cep';
      case 'trava_so_fibra': return 'so_fibra';
      default: return 'movel_sem_fibra';
    }
  }, [aval?.gate.acao]);

  const [modo, setModo] = useState<Modo>(modoInicial);
  const [escolha, setEscolha] = useState<'tem' | 'nao_vende' | 'sem_viabilidade' | 'gerente' | null>(null);
  const [checandoCep, setChecandoCep] = useState(false);

  // Campos
  const [cep, setCep] = useState('');
  const [numeroContrato, setNumeroContrato] = useState('');
  const [endereco, setEndereco] = useState('');
  const [documento, setDocumento] = useState<'CNPJ' | 'CPF'>('CNPJ');
  const [cpf, setCpf] = useState('');
  const [nome, setNome] = useState('');
  const [qtdLinhas, setQtdLinhas] = useState<string>(String(aval?.cobertura.qtdLinhasVoz ?? ''));
  const [territorio, setTerritorio] = useState(aval?.cobertura.territorioAace ?? '');
  const [motivo, setMotivo] = useState('');
  const [confirmaEndereco, setConfirmaEndereco] = useState(false);
  // "Não tem viabilidade de fibra": print da consulta de cobertura + confirmação RF
  const [provaUrls, setProvaUrls] = useState<string[]>([]);
  const [uploadingProva, setUploadingProva] = useState(false);
  const [confirmaProvaRf, setConfirmaProvaRf] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Endereço OFICIAL da empresa (Receita) — a fibra precisa estar nele.
  const cnpj14 = (aval?.cnpj || '').replace(/\D/g, '');
  const { data: rf } = useQuery({
    queryKey: ['rf-endereco-convergencia', cnpj14],
    enabled: open && cnpj14.length === 14,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from('cnpj_cache').select('payload').eq('cnpj', cnpj14).maybeSingle();
      return (data as any)?.payload ? parseCnpjPayload((data as any).payload) : null;
    },
  });
  const enderecoRF = rf
    ? `${rf.endereco}${rf.numero ? ', ' + rf.numero : ''}${rf.bairro ? ' - ' + rf.bairro : ''} - ${rf.cidade}/${rf.uf}`
    : '';

  // Reseta ao (re)abrir / trocar avaliação
  useEffect(() => {
    setModo(modoInicial);
    setEscolha(null);
    setCep('');
    setNumeroContrato(''); setEndereco(''); setDocumento('CNPJ'); setCpf(''); setNome('');
    setQtdLinhas(String(aval?.cobertura.qtdLinhasVoz ?? ''));
    setTerritorio(aval?.cobertura.territorioAace ?? '');
    setMotivo('');
    setConfirmaEndereco(false);
    setProvaUrls([]);
    setUploadingProva(false);
    setConfirmaProvaRf(false);
    setLightboxUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aval, modoInicial, open]);

  if (!aval) return null;

  // Upload de UM OU MAIS anexos: prints (imagem) E/OU gravação da ligação (áudio/mp3).
  const enviarProvas = async (files: File[]) => {
    const ok = files.filter((f) => f.type.startsWith('image/') || f.type.startsWith('audio/'));
    if (!ok.length) {
      toast.error('Anexe imagem (print da cobertura) ou áudio (gravação da ligação).');
      return;
    }
    setUploadingProva(true);
    try {
      for (const file of ok) {
        const ext = (file.name.split('.').pop() || 'png').toLowerCase();
        const path = `convergencia-provas/${cnpj14 || 'sem-cnpj'}/${Date.now()}-${Math.round(Math.random() * 1e6)}.${ext}`;
        const { error } = await supabase.storage.from('whatsapp-media').upload(path, file, { contentType: file.type, upsert: true });
        if (error) throw error;
        const { data } = supabase.storage.from('whatsapp-media').getPublicUrl(path);
        setProvaUrls((prev) => [...prev, data.publicUrl]);
      }
      toast.success(ok.length > 1 ? `${ok.length} anexos enviados.` : 'Anexo enviado.');
    } catch (e: any) {
      toast.error(`Falha ao anexar print: ${e?.message || e}`);
    } finally {
      setUploadingProva(false);
    }
  };

  const submeterCep = async () => {
    setChecandoCep(true);
    try {
      const tem = await coberturaPorCep(cep);
      if (tem) {
        // Tem cobertura → cai no fluxo "móvel sem fibra"
        setModo('movel_sem_fibra');
        setEscolha(null);
      } else {
        // Sem cobertura → passa (só móvel, sem oportunidade)
        onResolver({
          acao: 'passa', status: null, classificacao: 'SO_MOVEL',
          cepInformado: cep.replace(/\D/g, ''), coberturaInformada: false,
        });
      }
    } finally {
      setChecandoCep(false);
    }
  };

  const submeterMovelSemFibra = () => {
    if (escolha === 'tem') {
      // Só CNPJ conta como convergência. Endereço = o oficial da Receita (confirmado).
      const r = resolverMovelSemFibra({ tipo: 'cliente_tem_fibra', numeroContrato, endereco: enderecoRF, documento: 'CNPJ', provaUrls });
      onResolver({ ...r, cepInformado: cep ? cep.replace(/\D/g, '') : null, coberturaInformada: cep ? true : null });
    } else if (escolha === 'sem_viabilidade') {
      // NÃO tem viabilidade técnica → print + BKO/gerência validam (sem desconto se confirmado).
      onResolver(resolverMovelSemFibra({ tipo: 'enviar_gerente', motivo, motivoTipo: 'sem_viabilidade_tecnica', provaUrl: provaUrls[0] ?? null, provaUrls, confirmouRf: confirmaProvaRf }));
    } else {
      // TEM viabilidade mas optou por não vender → gerência aprova, COM desconto (sem print).
      onResolver(resolverMovelSemFibra({ tipo: 'enviar_gerente', motivo, motivoTipo: 'nao_venda_comercial' }));
    }
  };

  const submeterSoFibra = () => {
    if (escolha === 'tem') {
      onResolver(resolverSoFibra({ tipo: 'cliente_tem_movel', qtdLinhas: Number(qtdLinhas) || 0, territorio }, aval.cobertura));
    } else {
      onResolver(resolverSoFibra({ tipo: 'enviar_gerente', motivo }, aval.cobertura));
    }
  };

  // (01/09/2026) O print virou obrigatorio: sem prova o BKO validava so com o
  // numero do contrato digitado. Eram 861 declaracoes e zero provas.
  const formTemFibraValido = !!numeroContrato.trim() && !!enderecoRF && confirmaEndereco && provaUrls.length > 0;
  const formTemMovelValido = Number(qtdLinhas) > 0 && territorio.trim();

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentHighPriority className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            Convergência — confirme antes de fechar
          </DialogTitle>
          <DialogDescription>
            {modo === 'cep' && 'Não foi possível determinar a cobertura de fibra deste CNPJ. Informe o CEP de instalação.'}
            {modo === 'movel_sem_fibra' && 'Este CNPJ tem cobertura de fibra mas o pedido é só de móvel. O cliente já tem fibra ou vai só o móvel?'}
            {modo === 'so_fibra' && 'Pedido só de fibra. O cliente já tem móvel no CNPJ ou vai só a fibra?'}
          </DialogDescription>
        </DialogHeader>

        {/* MODO: pedir CEP */}
        {modo === 'cep' && (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>CEP de instalação</Label>
              <Input value={cep} onChange={(e) => setCep(e.target.value)} placeholder="00000-000" maxLength={9} />
            </div>
          </div>
        )}

        {/* MODO: móvel com cobertura, sem fibra — 3 caminhos */}
        {modo === 'movel_sem_fibra' && (
          <div className="space-y-3">
            <div className="grid gap-2">
              <Button variant={escolha === 'tem' ? 'default' : 'outline'} size="sm" className="justify-start" onClick={() => setEscolha('tem')}>
                <Wifi className="h-4 w-4 mr-2 shrink-0" /> Cliente já tem fibra
              </Button>
              <Button variant={escolha === 'nao_vende' ? 'default' : 'outline'} size="sm" className="justify-start whitespace-normal text-left h-auto py-2" onClick={() => setEscolha('nao_vende')}>
                <Building2 className="h-4 w-4 mr-2 shrink-0" /> Tem viabilidade, mas não vou vender fibra <span className="text-[10px] opacity-70 ml-1">(com desconto)</span>
              </Button>
              <Button variant={escolha === 'sem_viabilidade' ? 'default' : 'outline'} size="sm" className="justify-start whitespace-normal text-left h-auto py-2" onClick={() => setEscolha('sem_viabilidade')}>
                <WifiOff className="h-4 w-4 mr-2 shrink-0" /> Não tem viabilidade técnica <span className="text-[10px] opacity-70 ml-1">(print · BKO confere)</span>
              </Button>
            </div>

            {escolha === 'nao_vende' && (
              <div className="space-y-2 rounded-md border p-3">
                <p className="text-[11px] text-primary dark:text-primary">
                   Há cobertura de fibra no endereço da empresa. Como você vai vender só móvel, o pedido segue para o BKO e entra a <strong>penalidade de convergência</strong> na sua produtividade.
                </p>
                <div className="space-y-1">
                  <Label>Justifique *</Label>
                  <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Por que não vai vender a fibra neste cliente?" />
                </div>
              </div>
            )}

            {escolha === 'tem' && (
              <div className="space-y-2 rounded-md border p-3">
                <div className="space-y-1">
                  <Label>Nº do contrato da fibra</Label>
                  <Input value={numeroContrato} onChange={(e) => setNumeroContrato(e.target.value)} placeholder="Contrato da fixa já instalada (CNPJ ou CPF)" />
                </div>
                <div className="space-y-1">
                  <Label className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Endereço da fibra = endereço oficial da empresa</Label>
                  <div className="rounded-md bg-muted px-2 py-1.5 text-sm">
                    {enderecoRF || 'Endereço oficial não encontrado — atualize a Receita na aba Contato do lead.'}
                  </div>
                  <label className="flex items-start gap-2 text-xs mt-1 cursor-pointer">
                    <Checkbox checked={confirmaEndereco} onCheckedChange={(v) => setConfirmaEndereco(!!v)} disabled={!enderecoRF} className="mt-0.5" />
                    <span>Confirmo que a fibra está <strong>neste endereço</strong> (o oficial da empresa, acima).</span>
                  </label>
                </div>
                <p className="text-[11px] text-primary dark:text-primary">
                   O que vale é a fibra estar <strong>neste endereço</strong> (o oficial da empresa). Pode estar no CNPJ ou no CPF — só precisa ser no mesmo endereço da empresa.
                </p>

                <div className="space-y-1.5 rounded-md border border-dashed p-2.5">
                  <Label className="text-xs">Print do contrato da fibra *</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Anexe o <strong>print que mostra o contrato {numeroContrato.trim() || '—'} ativo neste endereço</strong>.
                    Sem ele o BKO não tem como conferir se a fibra existe — e a convergência não é aprovada.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    className="hidden"
                    onChange={(e) => { const fs = Array.from(e.target.files ?? []); if (fs.length) enviarProvas(fs); e.target.value = ''; }}
                  />
                  {provaUrls.length > 0 && (
                    <ul className="space-y-1">
                      {provaUrls.map((url, i) => (
                        <li key={url} className="flex items-center justify-between gap-2 rounded bg-muted px-2 py-1 text-[11px]">
                          <span className="truncate">Prova {i + 1}</span>
                          <button
                            type="button"
                            className="text-destructive hover:underline"
                            onClick={() => setProvaUrls((prev) => prev.filter((u) => u !== url))}
                          >remover</button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button type="button" variant="outline" size="sm" disabled={uploadingProva} onClick={() => fileInputRef.current?.click()}>
                    {uploadingProva ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                    {provaUrls.length > 0 ? 'Anexar mais' : 'Anexar print do contrato'}
                  </Button>
                </div>

                <p className="text-[11px] text-muted-foreground">
                  Depois de fechar, isso vai para <strong>aprovação do BKO responsável</strong>, que confere o contrato contra o print antes de a convergência valer.
                </p>
              </div>
            )}

            {escolha === 'sem_viabilidade' && (
              <div className="space-y-3 rounded-md border p-3">
                {/* O que a Claro/sistema informa × o endereço oficial */}
                <div className="rounded-md bg-success/10 border border-success/30 p-2 text-xs space-y-1">
                  <div className="flex items-center gap-1.5 font-semibold text-success">
                    <Wifi className="h-3.5 w-3.5" /> A Claro informa: TEM cobertura de fibra
                  </div>
                  <div className="flex items-start gap-1 text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>Endereço da Receita: <span className="text-foreground font-medium">{enderecoRF || '—'}</span></span>
                  </div>
                </div>

                <p className="text-[11px] text-primary dark:text-primary">
                   Como o sistema diz que TEM cobertura aqui, anexe o <strong>print da consulta de cobertura</strong> provando que NÃO tem viabilidade <strong>neste endereço da Receita</strong>. Pode anexar <strong>vários arquivos</strong> — prints e também a <strong>gravação da ligação (MP3)</strong>. O supervisor analisa e o BKO confirma a cobertura.
                </p>
                <p className="rounded-md border border-danger/30 bg-danger/5 px-2 py-1.5 text-[11px] text-danger">
                  Não use esta opção quando o cliente não quer fibra, tem fidelidade ou prefere outra operadora. Esses motivos são comerciais e devem ser marcados em <strong>“Tem viabilidade, mas não vou vender fibra”</strong>.
                </p>

                {/* Upload de vários anexos: prints (imagem) e/ou gravação (áudio) */}
                <div className="space-y-1.5">
                  <Label>Prints da cobertura / gravação da ligação *</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,audio/*"
                    multiple
                    className="hidden"
                    onChange={(e) => { const fs = Array.from(e.target.files ?? []); if (fs.length) enviarProvas(fs); e.target.value = ''; }}
                  />
                  {provaUrls.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {provaUrls.map((url, i) => {
                        const isAudio = /\.(mp3|wav|ogg|m4a|aac|opus|webm)(\?|$)/i.test(url);
                        return (
                          <div key={url} className="relative group rounded border bg-muted/30 p-1">
                            {isAudio ? (
                              <AudioPlayer src={url} className="max-w-[300px]" />
                            ) : (
                              <button type="button" onClick={() => setLightboxUrl(url)} className="relative block">
                                <img src={url} alt={`Anexo ${i + 1}`} className="h-16 w-16 object-cover rounded" />
                                <span className="absolute inset-0 grid place-items-center bg-black/40 opacity-0 group-hover:opacity-100 rounded text-white text-[10px] transition-opacity">ampliar</span>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setProvaUrls((prev) => prev.filter((u) => u !== url))}
                              className="absolute -top-1.5 -right-1.5 bg-danger text-white rounded-full h-5 w-5 grid place-items-center shadow"
                              title="Remover"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <Button type="button" variant="outline" size="sm" disabled={uploadingProva} onClick={() => fileInputRef.current?.click()}>
                    {uploadingProva ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                    {provaUrls.length > 0 ? 'Anexar mais (print / áudio)' : 'Anexar print ou gravação'}
                  </Button>
                </div>

                {/* Confirmação de que o print é do endereço da Receita */}
                <label className="flex items-start gap-2 text-xs cursor-pointer">
                  <Checkbox checked={confirmaProvaRf} onCheckedChange={(v) => setConfirmaProvaRf(!!v)} disabled={!provaUrls.length} className="mt-0.5" />
                  <span>Confirmo que o print é da consulta <strong>neste endereço da Receita Federal</strong> (acima).</span>
                </label>

                <div className="space-y-1">
                  <Label>Justificativa (o que aconteceu na consulta?) *</Label>
                  <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Ex.: consultei o CEP/endereço da Receita e não há viabilidade técnica de fibra…" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* MODO: só fibra */}
        {modo === 'so_fibra' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button variant={escolha === 'tem' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => setEscolha('tem')}>
                Cliente já tem móvel
              </Button>
              <Button variant={escolha === 'gerente' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => setEscolha('gerente')}>
                Vai só a fibra
              </Button>
            </div>

            {escolha === 'tem' && (
              <div className="space-y-2 rounded-md border p-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label>Nº de linhas móvel</Label><Input type="number" value={qtdLinhas} onChange={(e) => setQtdLinhas(e.target.value)} /></div>
                  <div className="space-y-1"><Label>Nome do parceiro Claro Empresas</Label><Input value={territorio} onChange={(e) => setTerritorio(e.target.value)} placeholder="Parceiro do móvel" /></div>
                </div>
                {aval.cobertura.territorioNosso === false && (
                  <p className="text-xs text-primary">Território de outro parceiro → vira solicitação de carteira automática.</p>
                )}
              </div>
            )}

            {escolha === 'gerente' && (
              <div className="space-y-1 rounded-md border p-3">
                <Label>Justifique</Label>
                <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>Cancelar</Button>
          {modo === 'cep' && (
            <Button onClick={submeterCep} disabled={checandoCep || cep.replace(/\D/g, '').length !== 8}>
              {checandoCep && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Verificar cobertura
            </Button>
          )}
          {modo === 'movel_sem_fibra' && (
            <Button
              onClick={submeterMovelSemFibra}
              disabled={
                processing || uploadingProva || !escolha ||
                (escolha === 'tem'
                  ? !formTemFibraValido
                  : escolha === 'sem_viabilidade'
                    ? (!motivo.trim() || !provaUrls.length || !confirmaProvaRf)
                    : !motivo.trim())
              }
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {escolha === 'tem'
                ? 'Confirmar e fechar'
                : escolha === 'sem_viabilidade'
                  ? 'Fechar e enviar ao BKO'
                  : 'Fechar e enviar ao BKO (com desconto)'}
            </Button>
          )}
          {modo === 'so_fibra' && (
            <Button
              onClick={submeterSoFibra}
              disabled={processing || !escolha || (escolha === 'tem' ? !formTemMovelValido : !motivo.trim())}
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {escolha === 'gerente' ? 'Fechar e enviar ao BKO' : 'Confirmar e fechar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContentHighPriority>
    </Dialog>
    <ImageLightbox src={lightboxUrl} alt="Print da consulta de cobertura" onClose={() => setLightboxUrl(null)} />
    </>
  );
}
